import { useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import ReminderService from "../Services/ReminderService";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseJSON } from "date-fns";
import ReminderModel from "../Models/ReminderModel";
import nlBE from "date-fns/locale/nl-BE";
import "react-datepicker/dist/react-datepicker.css";
import GraphService from "../Services/GraphService";
import PeopleService from "../Services/PeopleService";
import PersonModel from "../Models/PersonModel";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";

interface ReminderModalProps {
  code: string;
  reminder: ReminderModel;
  afterClose: Function;
}

const ReminderModal = (props: ReminderModalProps) => {
  const { instance } = useMsal();

  const [visible, setVisible] = useState<boolean>(true);

  const [reminder, setReminder] = useState<ReminderModel>(props.reminder);

  const [people, setPeople] = useState<PersonModel[]>([]);

  const reminderService = useMemo(
    () => new ReminderService(instance),
    [instance]
  );
  const graphService = useMemo(() => new GraphService(instance), [instance]);
  const peopleService = useMemo(() => new PeopleService(instance), [instance]);

  registerLocale("nlBE", nlBE);

  useEffect(() => {
    const fetchData = async () => {
      let people = await peopleService.getOwners();

      people = people.sort((r1, r2) => {
        return (r1.name ?? "").localeCompare(r2.name ?? "");
      });

      setPeople(people);
    };

    fetchData();
  }, [props, props.code, reminderService, graphService, peopleService]);

  const modalToggle = () => {
    setVisible((visible) => {
      return !visible;
    });

    props.afterClose();
  };

  const saveReminder = async () => {
    try {
      await reminderService.upsert(reminder);
      modalToggle();
    } catch (error) {
      toast.error("Fout bij het bewaren van de data.");
    }
  };

  return (
    <>
      <Modal isOpen={visible} toggle={() => modalToggle()}>
        <ModalHeader toggle={() => modalToggle()}>Reminder</ModalHeader>
        <ModalBody>
          <form>
            <div className="form-group">
              <label className="my-1 mr-2">Datum</label>

              <DatePicker
                locale="nlBE"
                selected={
                  reminder.date === undefined
                    ? new Date()
                    : parseJSON(reminder.date)
                }
                onChange={(date) =>
                  setReminder({
                    ...reminder,
                    date:
                      date === null
                        ? undefined
                        : format(date, "yyyy-MM-dd") + "T00:00:00.000Z",
                  })
                }
                dateFormat="dd/MM/yyyy"
                customInput={
                  <input type="text" className="form-control my-1 mr-sm-2" />
                }
              />
            </div>
            <div className="form-group">
              <label className="my-1 mr-2">Reminder</label>
              <textarea
                className="form-control my-1 mr-sm-2"
                title="Opmerkingen"
                style={{ height: "200px" }}
                onChange={(event) =>
                  setReminder({ ...reminder, description: event.target.value })
                }
                value={reminder?.description}
              ></textarea>
            </div>
            <div className="form-group">
              <label className="" htmlFor="personSelect">
                Persoon
              </label>
              <select
                className="form-control"
                id="personSelect"
                defaultValue={reminder.personId ?? ""}
                onChange={(event) =>
                  setReminder({ ...reminder, personId: event.target.value })
                }
              >
                {people.map((person) => {
                  return (
                    <option value={person.id} key={person.id}>
                      {person.name}
                    </option>
                  );
                })}
              </select>
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => saveReminder()}>
            Bewaren
          </Button>{" "}
          <Button color="secondary" onClick={() => modalToggle()}>
            Sluiten
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ReminderModal;
